import React from "react";
import { Leaf, P } from "./shell";
import { VELLUM as T, display, mono, body, SheetHead } from "./tokens";

/* A-06 · revisions - the undertakings, each marked with whether it is
   actually operating. A register that will not say which of its own claims
   are in force has no business asking anyone to trust the entries.

   Drawn as a drawing's revision table: code, what changed, and its status in
   the right-hand column. */

interface Undertaking {
  body: string;
  note?: string;
  inForce: boolean;
}

const UNDERTAKINGS: Undertaking[] = [
  {
    body: "Nothing can be paid into a higher position in the register.",
    note: "There is no mechanism for it, and there will not be one.",
    inForce: true,
  },
  {
    body: "The register is free for scholars, and free for institutions while it is being built.",
    note: "If that changes, it changes in public and for everyone at once.",
    inForce: true,
  },
  {
    body: "No figure appears here that we cannot stand behind.",
    note: "It is why this page shows the register at its real size, whatever that happens to be today.",
    inForce: true,
  },
  {
    body: "Every listing will be read by a working academic before it goes up.",
    note: "Not yet in force. The editorial desk is being assembled, and until it is reading, this stays an undertaking rather than a claim.",
    inForce: false,
  },
];

export const LeafUndertakings: React.FC = () => (
  <Leaf
    code="§ A-06 · revisions"
    measure={
      <>
        <SheetHead t={T} code="rev. table">
          What the register undertakes.
        </SheetHead>

        <p style={{ ...body(T), fontSize: 16.5, maxWidth: 600, marginBottom: 26 }}>
          Three of these are in force today. One is not, and is marked so. A register is only worth
          reading if it is straight about its own state.
        </p>

        <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {UNDERTAKINGS.map((u, i) => (
            <li
              key={u.body}
              className={`${P}-under`}
              style={{
                display: "grid",
                gridTemplateColumns: "54px minmax(0,1fr) 156px",
                gap: 18,
                padding: "18px 0",
                borderTop: `1px solid ${T.rule}`,
              }}
            >
              <span style={mono(T, T.accent)}>{String(i + 1).padStart(2, "0")}</span>
              <span>
                <span style={{ ...display(T, "clamp(17px, 2.1vw, 23px)"), display: "block", lineHeight: 1.2 }}>
                  {u.body}
                </span>
                {u.note && <p style={{ ...body(T), fontSize: 14.5, marginTop: 8 }}>{u.note}</p>}
              </span>
              <span
                style={{
                  ...mono(T, u.inForce ? T.mute : T.accent),
                  textAlign: "right",
                  display: "inline-flex",
                  alignItems: "baseline",
                  justifyContent: "flex-end",
                  gap: 7,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: u.inForce ? T.mute : T.accent,
                    display: "inline-block",
                  }}
                />
                {u.inForce ? "In force" : "Not yet in force"}
              </span>
            </li>
          ))}
        </ol>
      </>
    }
    margin={
      <div style={{ borderTop: `1.5px solid ${T.ink}`, paddingTop: 14 }}>
        <div style={{ ...mono(T, T.ink), marginBottom: 8 }}>Standing note</div>
        <p style={{ ...body(T), fontSize: 13.5 }}>
          Acanetic has not opened publicly. There are no placement figures, no partner list and no
          testimonials on this page because there are none to give.
        </p>
      </div>
    }
  />
);
